import React, { useEffect, useMemo, useState } from 'react';
import Loading from '../../common/Loading/Loading';
import FormInput from '../../common/FormInput/FormInput';
import FormSelect from '../../common/FormSelect/FormSelect';
import Checkbox from '../../common/Checkbox/Checkbox';
import type { SubscriptionPlan, StudentSubscription } from '../../../types';
import { SubscriptionPlanService } from '../../../services';
import { formatCurrency, formatDate } from '../../../utils';
import { parsePlanTransitionStartDate } from '../../../utils/subscriptionLifecycle';
import { getTodayBrtDateOnly } from '../../../utils/subscriptionPause';
import {
  GATEWAY_PROVIDERS,
  PAYMENT_SYSTEM_LABELS,
  type GatewayProvider,
} from '../../../types/payment';
import {
  ASAAS_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS,
  CYCLE_LABELS,
  STRIPE_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS,
} from './paymentDetails.constants';
import PlanNameWithProject, { createPlanSelectFormatters } from '../../common/PlanNameWithProject/PlanNameWithProject';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import ModalSummary from './ModalSummary';
import styles from './CreateSubscriptionModal.module.css';

export type PlanTransitionGateway = GatewayProvider | 'pix';

interface SchedulePlanTransitionModalProps {
  open: boolean;
  loading: boolean;
  gateway: PlanTransitionGateway;
  currentSubscription: StudentSubscription;
  suggestedStartDate: string;
  onConfirm: (
    plan: SubscriptionPlan,
    effectiveStartDate: string,
    nextGateway: PlanTransitionGateway,
    paymentMethod: 'pix' | 'credit_card' | null,
  ) => void;
  onCancel: () => void;
}

const SchedulePlanTransitionModal: React.FC<SchedulePlanTransitionModalProps> = ({
  open,
  loading,
  gateway,
  currentSubscription,
  suggestedStartDate,
  onConfirm,
  onCancel,
}) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedGateway, setSelectedGateway] = useState<PlanTransitionGateway>(gateway);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'pix' | 'credit_card'>('credit_card');
  const [immediate, setImmediate] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [today, setToday] = useState(getTodayBrtDateOnly);

  useEffect(() => {
    if (!open) {
      setSelectedPlanId('');
      setSelectedGateway(gateway);
      setSelectedPaymentMethod(
        currentSubscription.payment_method === 'pix' || currentSubscription.payment_method === 'credit_card'
          ? currentSubscription.payment_method
          : 'credit_card',
      );
      setImmediate(true);
      setSelectedDate('');
      setError(null);
      return;
    }

    const currentToday = getTodayBrtDateOnly();
    const parsedSuggested = parsePlanTransitionStartDate(suggestedStartDate);
    const nextDate = parsedSuggested && parsedSuggested > currentToday ? parsedSuggested : currentToday;
    setToday(currentToday);
    setImmediate(nextDate === currentToday);
    setSelectedDate(nextDate);
    setSelectedGateway(gateway);
    setSelectedPaymentMethod(
      currentSubscription.payment_method === 'pix' || currentSubscription.payment_method === 'credit_card'
        ? currentSubscription.payment_method
        : 'credit_card',
    );
    setError(null);

    setPlansLoading(true);
    SubscriptionPlanService.getSelectablePlans()
      .then((items) => {
        setPlans(items);
      })
      .finally(() => setPlansLoading(false));
  }, [open, suggestedStartDate, gateway, currentSubscription.payment_method]);

  useEscapeToClose(onCancel, open && !loading);

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.id) === selectedPlanId) ?? null,
    [plans, selectedPlanId],
  );

  const resolvedStartDate = immediate ? today : selectedDate;
  const currentCycleLabel = CYCLE_LABELS[currentSubscription.billing_cycle] ?? currentSubscription.billing_cycle;
  const selectedPlanCycle = selectedPlan?.billing_cycle.trim().toLowerCase() ?? '';
  const selectedPlanCycleLabel = selectedPlan
    ? CYCLE_LABELS[selectedPlanCycle] ?? CYCLE_LABELS[selectedPlan.billing_cycle] ?? selectedPlan.billing_cycle
    : '';
  const selectedPlanIsSinglePayment = Boolean(selectedPlan?.is_single_payment)
    || (selectedPlanCycle !== '' && selectedPlanCycle !== 'monthly');

  const gatewayOptions = [
    ...GATEWAY_PROVIDERS.map((item) => ({
      value: item,
      label: PAYMENT_SYSTEM_LABELS[item],
    })),
    { value: 'pix' as const, label: PAYMENT_SYSTEM_LABELS.pix },
  ];

  const showsAsaasPaymentMethod = selectedGateway === 'asaas';
  const showsStripePaymentMethod = selectedGateway === 'stripe';
  const paymentMethodValid = !showsAsaasPaymentMethod
    || selectedPaymentMethod === 'pix'
    || selectedPaymentMethod === 'credit_card';
  const canConfirm = Boolean(selectedPlanId) && !plansLoading && paymentMethodValid;

  const handleConfirm = () => {
    if (!selectedPlan) {
      return;
    }

    if (showsAsaasPaymentMethod && selectedPaymentMethod !== 'pix' && selectedPaymentMethod !== 'credit_card') {
      setError('Informe a forma de pagamento (Pix ou Cartão de crédito).');
      return;
    }

    const paymentMethod: 'pix' | 'credit_card' | null = showsAsaasPaymentMethod
      ? selectedPaymentMethod
      : showsStripePaymentMethod
        ? 'credit_card'
        : null;

    if (!immediate) {
      const parsedDate = parsePlanTransitionStartDate(selectedDate);
      if (!parsedDate) {
        setError('Informe a data em que a troca deve acontecer.');
        return;
      }
      if (parsedDate < today) {
        setError('A data da troca não pode ser anterior a hoje.');
        return;
      }
      onConfirm(selectedPlan, parsedDate, selectedGateway, paymentMethod);
      return;
    }

    onConfirm(selectedPlan, today, selectedGateway, paymentMethod);
  };

  if (!open) {
    return null;
  }

  const planOptions = plans.map((plan) => ({ value: String(plan.id), label: plan.name }));
  const planSelectFormatters = createPlanSelectFormatters(plans);

  return (
    <div className={styles.overlay}>
      <div
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        style={{ width: 520, alignItems: 'stretch' }}
      >
        <div className={styles['icon-circle']} style={{ alignSelf: 'center' }}>
          <span className="material-symbols-outlined">swap_horiz</span>
        </div>

        <h3 className={styles.title}>Trocar plano</h3>
        <p className={styles.message}>
          {immediate
            ? 'A nova assinatura começa assim que você confirmar.'
            : resolvedStartDate
              ? `A nova assinatura começará em ${formatDate(resolvedStartDate)}.`
              : 'Informe a data em que a nova assinatura deve começar.'}
        </p>

        <ModalSummary
          items={[
            {
              label: 'Plano atual',
              value: (
              <PlanNameWithProject
                name={currentSubscription.plan_name || 'Plano atual'}
                project={currentSubscription.project}
              />
              ),
            },
            { label: 'Ciclo atual', value: currentCycleLabel },
            { label: 'Gateway atual', value: PAYMENT_SYSTEM_LABELS[gateway] },
            {
              label: 'Novo gateway',
              value: PAYMENT_SYSTEM_LABELS[selectedGateway],
            },
            {
              label: 'Data efetiva',
              value: resolvedStartDate ? formatDate(resolvedStartDate) : '—',
            },
          ]}
        />

        <div className={styles['select-wrap']}>
          <FormSelect
            id="transition-gateway"
            label="Gateway de destino"
            options={gatewayOptions}
            value={selectedGateway}
            onChange={(event) => {
              const nextGateway = event.target.value as PlanTransitionGateway;
              setSelectedGateway(nextGateway);
              if (nextGateway === 'asaas' || nextGateway === 'stripe') {
                setSelectedPaymentMethod('credit_card');
              }
              if (error) {
                setError(null);
              }
            }}
            disabled={loading}
          />
        </div>

        {showsAsaasPaymentMethod && (
          <div className={styles['select-wrap']}>
            <FormSelect
              id="transition-payment-method"
              label="Forma de pagamento"
              options={ASAAS_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS}
              value={selectedPaymentMethod}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedPaymentMethod(
                  value === 'pix' || value === 'credit_card' ? value : 'credit_card',
                );
                if (error) {
                  setError(null);
                }
              }}
              disabled={loading}
            />
          </div>
        )}

        {showsStripePaymentMethod && (
          <div className={styles['select-wrap']}>
            <FormSelect
              id="transition-payment-method-stripe"
              label="Forma de pagamento"
              options={STRIPE_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS}
              value="credit_card"
              onChange={() => {
                setSelectedPaymentMethod('credit_card');
              }}
              disabled={loading}
              readOnly
            />
          </div>
        )}

        {selectedGateway === 'paypal' && (
          <p className={styles.message}>
            No PayPal a cobrança pode exigir aprovação pelo link da assinatura.
          </p>
        )}

        <Checkbox
          variant="classic"
          label="Imediatamente ao concluir"
          checked={immediate}
          disabled={loading}
          onPress={() => {
            setImmediate((current) => !current);
            setError(null);
          }}
        />

        {!immediate && (
          <FormInput
            id="transition-start-date"
            label="Data da troca"
            type="date"
            required
            value={selectedDate}
            min={today}
            onChange={(event) => {
              setSelectedDate(event.target.value);
              if (error) {
                setError(null);
              }
            }}
            disabled={loading}
          />
        )}

        <div className={styles['select-wrap']}>
          {plansLoading ? (
            <Loading />
          ) : (
            <FormSelect
              id="transition-plan"
              label="Novo plano"
              options={planOptions}
              value={selectedPlanId}
              onChange={(event) => setSelectedPlanId(event.target.value)}
              disabled={loading}
              triggerFormat={planSelectFormatters.triggerFormat}
              optionFormat={planSelectFormatters.optionFormat}
            />
          )}
        </div>

        {selectedPlan && (
          <ModalSummary
            items={[
              {
                label: 'Novo valor',
                value: `${formatCurrency(selectedPlan.price_cents / 100)}/${selectedPlanCycleLabel.toLowerCase() || 'mês'}`,
              },
              { label: 'Novo ciclo', value: selectedPlanCycleLabel },
              { label: 'À vista', value: selectedPlanIsSinglePayment ? 'Sim' : 'Não' },
              {
                label: 'Fidelidade',
                value: selectedPlan.loyalty_months > 0
                  ? `${selectedPlan.loyalty_months} ${selectedPlan.loyalty_months === 1 ? 'mês' : 'meses'}`
                  : 'Sem fidelidade',
              },
            ]}
          />
        )}

        {error && <p className={styles.validation}>{error}</p>}

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-cancel']}`}
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-confirm']}`}
            onClick={handleConfirm}
            disabled={loading || !canConfirm}
          >
            {loading
              ? (immediate ? 'Trocando...' : 'Agendando...')
              : (immediate ? 'Trocar agora' : 'Agendar troca')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchedulePlanTransitionModal;
